import React, { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';

// Formats seconds into human-friendly units
export const formatDurationHuman = (seconds) => {
  if (seconds == null || isNaN(seconds)) return '';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return sec ? `${m} min ${sec}s` : `${m} min`;
  }
  const h = Math.floor(s / 3600);
  const rem = s % 3600;
  const m = Math.floor(rem / 60);
  return m ? `${h} h ${m} min` : `${h} h`;
};

// Compute adjusted duration based on servings vs originalServings
export const getAdjustedDuration = (timing, servings, originalServings) => {
  const base = Number(timing?.duration) || 0;
  const additionalTimePerPersonInSecond = Number(timing?.adjustmentFactor?.additionalTimePerPersonInSecond) || 0;
  const maxAdd = timing?.adjustmentFactor?.max;
  const deltaPeople = Math.max(0, (Number(servings) || 0) - (Number(originalServings) || 0));
  const add = additionalTimePerPersonInSecond > 0 ? additionalTimePerPersonInSecond * deltaPeople : 0;
  const cappedAdd = typeof maxAdd === 'number' && maxAdd >= 0 ? Math.min(add, maxAdd) : add;
  return base + cappedAdd;
};

// Heuristic: detect temperature-like content inside <Time> tags
// Examples: "200 °C", "180°C", "thermostat 6", "th. 7"
const isTemperatureLike = (str) => {
  if (!str) return false;
  const t = String(str);
  // Degrees with optional unit C/F
  if (/[°º]\s*(?:[cCfF])?/.test(t)) return true;
  // Oven thermostat notation
  if (/\b(?:thermostat|th\.?)[\s]*\d+/i.test(t)) return true;
  return false;
};

const slugify = (value) => {
  if (!value) return '';
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
};

const normalizeId = (value) => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'object') {
    if (value._id != null) return normalizeId(value._id);
    if (value.id != null) return normalizeId(value.id);
  }
  return null;
};

const buildIngredientIndex = (ingredients) => {
  const map = new Map();
  if (!Array.isArray(ingredients)) return map;

  ingredients.forEach((ingredient) => {
    if (!ingredient) return;

    const standardSource =
      (ingredient && typeof ingredient.standardIngredient === 'object')
        ? ingredient.standardIngredient
        : null;

    const primaryId =
      normalizeId(ingredient.standardIngredient) ||
      normalizeId(ingredient.standardIngredientId) ||
      normalizeId(standardSource);

    if (!primaryId) return;

    const candidateIds = new Set();
    const addId = (val) => {
      const id = normalizeId(val);
      if (id) candidateIds.add(id);
    };

    addId(primaryId);
    addId(ingredient.standardIngredient);
    addId(ingredient.standardIngredientId);
    if (standardSource) {
      addId(standardSource._id);
      addId(standardSource.id);
    }
    addId(ingredient.normalizedIngredientId);

    const displayName =
      (typeof ingredient.normalizedName === 'string' && ingredient.normalizedName.trim()) ||
      (typeof ingredient.name === 'string' && ingredient.name.trim()) ||
      (standardSource && typeof standardSource.name === 'string' && standardSource.name.trim()) ||
      (standardSource && typeof standardSource.label === 'string' && standardSource.label.trim()) ||
      '';

    const rawSlug =
      (typeof ingredient.standardIngredientSlug === 'string' && ingredient.standardIngredientSlug.trim()) ||
      (typeof ingredient.slug === 'string' && ingredient.slug.trim()) ||
      (standardSource && typeof standardSource.slug === 'string' && standardSource.slug.trim()) ||
      '';

    const resolvedSlug = rawSlug ? slugify(rawSlug) : slugify(displayName);

    candidateIds.forEach((id) => {
      if (!id || map.has(id)) return;
      const slug = resolvedSlug || slugify(id);
      const url = slug ? `/idees-de-repas/idees-recettes/${encodeURIComponent(slug)}/${encodeURIComponent(id)}` : null;
      map.set(id, {
        id,
        slug,
        name: displayName,
        url,
      });
    });
  });

  return map;
};

const splitIngredientSegments = (segment) => {
  const parts = [];
  if (segment == null || segment === '') return parts;
  
  // Nettoie d'abord les barres obliques inverses parasites
  const cleanedSegment = cleanEscapedTags(segment);
  
  const ingredientRegex = /<Ingredient\b([^>]*)>([\s\S]*?)<\/Ingredient>/gi;
  let last = 0;
  let match;

  while ((match = ingredientRegex.exec(cleanedSegment)) !== null) {
    if (match.index > last) {
      parts.push({ type: 'text', value: cleanedSegment.slice(last, match.index) });
    }
    const rawAttributes = match[1] || '';
    const idMatch = rawAttributes.match(/\bid\s*=\s*["']([^"']+)["']/i);
    const id = idMatch ? idMatch[1] : null;
    parts.push({ type: 'ingredient', id, text: match[2] || '' });
    last = match.index + match[0].length;
  }

  if (last < cleanedSegment.length) {
    parts.push({ type: 'text', value: cleanedSegment.slice(last) });
  }

  return parts;
};

// Nettoie les barres obliques inverses parasites dans les balises Ingredient et Time
const cleanEscapedTags = (text) => {
  if (!text) return text;
  
  // Nettoie les barres obliques inverses parasites dans les attributs des balises
  // Exemple: <Ingredient id=\"68cd7407a742f9fcd308d043\"> → <Ingredient id="68cd7407a742f9fcd308d043">
  return text
    .replace(/(<(?:Ingredient|Time)\b[^>]*?)\\"/g, '$1"')
    .replace(/(<(?:Ingredient|Time)\b[^>]*?)\\'/g, "$1'")
    // Nettoie aussi les barres obliques inverses en fin d'attributs
    .replace(/(<(?:Ingredient|Time)\b[^>]*?)\\"([^"]*?)\\"/g, '$1"$2"')
    .replace(/(<(?:Ingredient|Time)\b[^>]*?)\\'/g, "$1'");
};

// Renders a text with custom <Time id="...">..</Time> tags into React nodes
// Matching is done against the provided timings array (ListOfTimingsInStep)
const ExplanationWithTimes = ({ text, timings, servings, originalServings, onStartTimer, ingredients }) => {
  const ingredientIndex = useMemo(() => buildIngredientIndex(ingredients), [ingredients]);
  if (!text) return null;
  
  // Nettoie les barres obliques inverses parasites avant le parsing
  const cleanedText = cleanEscapedTags(text);
  
  const result = [];
  // Supporte <Time id="...">...</Time> et <Time>...</Time>
  const regex = /<Time\b([^>]*)>([\s\S]*?)<\/Time>/gi;
  let lastIndex = 0;
  let match;
  let key = 0;
  let usedIndexes = new Set();

  const nextKey = () => `seg-${key++}`;

  const renderParts = (parts, { wrapText = true } = {}) => {
    const nodes = [];
    parts.forEach((part) => {
      if (part.type === 'text') {
        if (part.value == null || part.value === '') return;
        if (wrapText) {
          nodes.push(<span key={nextKey()}>{part.value}</span>);
        } else {
          nodes.push(<Fragment key={nextKey()}>{part.value}</Fragment>);
        }
        return;
      }

      const idKey = normalizeId(part.id);
      const meta = idKey ? ingredientIndex.get(idKey) : null;
      const label = (part.text && part.text.trim()) || meta?.name || part.text || '';

      if (meta?.url) {
        nodes.push(
          <Link
            key={nextKey()}
            to={meta.url}
            target="_blank"
            className="text-[#87A96B] font-semibold underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-[#87A96B]/60 focus:ring-offset-1"
          >
            {label}
          </Link>
        );
      } else {
        nodes.push(
          <span key={nextKey()} className="font-semibold text-[#87A96B]">
            {label}
          </span>
        );
      }
    });
    return nodes;
  };

  const pushTextSegment = (segment) => {
    if (segment == null || segment === '') return;
    const parts = splitIngredientSegments(segment);
    const nodes = renderParts(parts);
    nodes.forEach((node) => result.push(node));
  };

  while ((match = regex.exec(cleanedText)) !== null) {
    const [full, rawAttributes = '', inner] = match;
    const idMatch = rawAttributes.match(/\bid\s*=\s*["']([^"']+)["']/i);
    const id = idMatch ? idMatch[1] : undefined;
    // preceding text
    pushTextSegment(cleanedText.slice(lastIndex, match.index));

    // If the content looks like a temperature, do not turn it into a timer
    if (isTemperatureLike(inner)) {
      pushTextSegment(inner);
      lastIndex = match.index + full.length;
      continue;
    }

    let timing = null;
    if (Array.isArray(timings) && timings.length > 0) {
      let timingIndex = -1;
      if (id != null && id !== '') {
        timingIndex = timings.findIndex((t) => String(t.id) === String(id));
        if (timingIndex !== -1) {
          timing = timings[timingIndex];
          usedIndexes.add(timingIndex);
        }
      }
      if (!timing) {
        // Fallback: associer dans l'ordre aux éléments non encore utilisés
        for (let i = 0; i < timings.length; i++) {
          if (!usedIndexes.has(i)) {
            timing = timings[i];
            usedIndexes.add(i);
            break;
          }
        }
      }
    }

    if (timing) {
      // Gérer nouveau schéma adjustmentFactor.additionalTimePerPersonInSecond
      const adjusted = getAdjustedDuration(
        {
          ...timing,
          adjustmentFactor: timing?.adjustmentFactor && {
            perPerson:
              timing.adjustmentFactor.additionalTimePerPersonInSecond ??
              timing.adjustmentFactor.perPerson ?? 0,
            max: timing.adjustmentFactor.max,
          },
        },
        servings,
        originalServings
      );
      const label = timing.label || 'Minuterie';
      const duration = formatDurationHuman(adjusted);
      // Replace inner content by recalculated duration
      const isClickable = typeof onStartTimer === 'function';
      const onClick = () => {
        if (!isClickable) return;
        const ok = window.confirm(`Démarrer une minuterie "${label}" de ${duration} ?`);
        if (ok) onStartTimer({ label, duration: adjusted });
      };
      result.push(
        <span key={`time-${key++}`} className="relative inline-flex items-center group">
          <span
            className={`px-1 rounded font-medium border ${isClickable ? 'cursor-pointer bg-yellow-200 hover:bg-yellow-300 border-yellow-300' : 'bg-yellow-100 border-yellow-200'} text-[#2C3E50]`}
            onClick={onClick}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            title={isClickable ? 'Cliquer pour démarrer une minuterie' : undefined}
          >
            {duration}
          </span>
          <span className="absolute -top-9 left-1/2 -translate-x-1/2 bg-[#2C3E50] text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
            {label}
          </span>
        </span>
      );
    } else {
      // Pas de correspondance côté données : garder le style visuel pour le texte balisé
      result.push(
        <span key={`time-${key++}`} className="relative inline-flex items-center">
          <span className="px-1 rounded font-medium border bg-yellow-100 border-yellow-200 text-[#2C3E50]">
            {(() => {
              const parts = splitIngredientSegments(inner);
              const children = renderParts(parts, { wrapText: false });
              if (children.length > 0) return children;
              return inner;
            })()}
          </span>
        </span>
      );
    }

    lastIndex = match.index + full.length;
  }

  // remaining text
  pushTextSegment(cleanedText.slice(lastIndex));

  return <>{result}</>;
};

export default ExplanationWithTimes;
